import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import moment from 'moment/moment';
import { RootState } from '../app.store';
import { getFiscalWeekStartDateList,getDailyGrainDateList } from '../../../Utils/DateUtils';
import { Constants } from '../../../common/constants';

export interface DateNavigationState {
  startDate: string;
  endDate: string;
  numberOfWeeks: number;
  fiscalWeekStartDateList: string[];
  dailyGrainDateList: string[];
  filteredDailyGrainDateList: string[]; // New: filtered based on work preferences
  workingDays: string[]; // New: store working days from work preferences
}

const initStartDate = new Date();
const initNumberOfWeeks = 2;
const initialDateNavigationState: DateNavigationState = {
    startDate: moment(initStartDate).utc().format(Constants.DATE_FORMAT),
    endDate:  moment(initStartDate).utc()
    .add(initNumberOfWeeks, 'weeks')
    .format(Constants.DATE_FORMAT),
    numberOfWeeks: initNumberOfWeeks,
    fiscalWeekStartDateList: getFiscalWeekStartDateList(initStartDate.toDateString(), initNumberOfWeeks),
    dailyGrainDateList: getDailyGrainDateList(initStartDate.toDateString(), initNumberOfWeeks),
    filteredDailyGrainDateList: getDailyGrainDateList(initStartDate.toDateString(), initNumberOfWeeks), // Default to all days
    workingDays: [], // Default to empty array
};

const filterDatesBasedOnWorkingDays = (dateList: string[], workingDays: string[]): string[] => {
  if (!workingDays || workingDays.length === 0) {
    return dateList; // Return all dates if no working days specified
  }
  
  return dateList.filter(dateStr => {
    const date = moment(dateStr, Constants.DATE_FORMAT);
    const dayOfWeek = date.format('dddd'); // Full day name (e.g., 'Monday')
    return workingDays.includes(dayOfWeek);
  });
};

export const dateNavigationSlice = createSlice({
  name: 'dateNavigationUpdate',
  initialState: initialDateNavigationState,
  reducers: {
    updateDates: (state, action: PayloadAction<{ startDate: string; numberOfWeeks: number }>) => {
      const startDate = new Date(action.payload.startDate);
      startDate.setHours(0, -startDate.getTimezoneOffset(), 0, 0);

      const endDate = new Date(startDate);
      endDate.setHours(0, -endDate.getTimezoneOffset(), 0, 0);

      state.startDate = moment(startDate).utc().format(Constants.DATE_FORMAT);
      state.endDate = moment(endDate).utc()
        .add(action.payload.numberOfWeeks, 'weeks')
        .format(Constants.DATE_FORMAT);
      state.numberOfWeeks = action.payload.numberOfWeeks;
      state.fiscalWeekStartDateList = getFiscalWeekStartDateList(action.payload.startDate, action.payload.numberOfWeeks);
      state.dailyGrainDateList = getDailyGrainDateList(action.payload.startDate, action.payload.numberOfWeeks, state.workingDays);
      
      // Update filtered list based on current working days
      state.filteredDailyGrainDateList = filterDatesBasedOnWorkingDays(state.dailyGrainDateList, state.workingDays);
    },
    updateWorkingDays: (state, action: PayloadAction<string[]>) => {
      state.workingDays = action.payload;
      // Regenerate daily grain date list with new working days
      state.dailyGrainDateList = getDailyGrainDateList(state.startDate, state.numberOfWeeks, action.payload);
      // Recalculate filtered dates based on new working days
      state.filteredDailyGrainDateList = filterDatesBasedOnWorkingDays(state.dailyGrainDateList, action.payload);
    },
  }
});

export const {
    updateDates,
    updateWorkingDays
} = dateNavigationSlice.actions;


export const fiscalWeekStartDateListSelector = (state: RootState) => state.dateNavigation.fiscalWeekStartDateList;
export const dailyGrainDateListSelector = (state: RootState) => state.dateNavigation.dailyGrainDateList;
export const filteredDailyGrainDateListSelector = (state: RootState) => state.dateNavigation.filteredDailyGrainDateList;
export const workingDaysSelector = (state: RootState) => state.dateNavigation.workingDays;

export const dateRangeSelector = (state: RootState) => ({
    startDate: state.dateNavigation.startDate,
    endDate: state.dateNavigation.endDate
});

export default dateNavigationSlice.reducer;
